<script setup lang="ts">
import type { DialogContentEmits, DialogContentProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import type { SheetVariants } from '.';
import { reactiveOmit } from '@vueuse/core';
import { X } from 'lucide-vue-next';
import { DialogClose, DialogContent, DialogOverlay, DialogPortal, useForwardPropsEmits } from 'reka-ui';
import { cn } from '@/lib/utils';
import { sheetVariants } from '.';

interface SheetContentProps extends DialogContentProps {
    class?: HTMLAttributes['class'];
    side?: SheetVariants['side'];
    /**
     * Suppress the built-in corner X (ITDEVELOP-3147). A full-screen sheet
     * supplies its own Cancel/Apply header, and a compact sheet dismisses by
     * scrim, Escape, or tap-to-pick — in both cases the X duplicates an
     * existing control and, at top-right, collides with it. Scrim and Escape
     * still dismiss, so this removes a duplicate affordance, never the only
     * one.
     */
    hideClose?: boolean;
}

defineOptions({
    inheritAttrs: false,
});

const props = defineProps<SheetContentProps>();

const emits = defineEmits<DialogContentEmits>();

const delegatedProps = reactiveOmit(props, 'class', 'side', 'hideClose');

const forwarded = useForwardPropsEmits(delegatedProps, emits);

// A sheet is a touch surface: letting the dialog auto-focus its first control
// makes reka highlight it and scrollIntoView it, which smooth-scrolls the page
// BEHIND the fixed sheet (ITDEVELOP-3147 QA: open a select sheet and the form
// crawls upward). Focus the sheet container itself, scroll-free — the focus
// trap, Tab order, Escape and scrim dismissal all still work.
function onOpenAutoFocus(e: Event) {
    e.preventDefault();
    (e.target as HTMLElement | null)?.focus({ preventScroll: true });
}
</script>

<template>
    <DialogPortal>
        <DialogOverlay
            :class="
                cn(
                    // --overlay-scrim, not the stock shadcn bg-black/80: sheets
                    // and dialogs must carry the same scrim weight in both
                    // themes (MR !312 review).
                    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-[var(--overlay-scrim)]',
                    '', // atNorth overrides
                )
            "
        />
        <DialogContent
            :class="
                cn(
                    sheetVariants({ side }),
                    'bg-lightest p-0', // atNorth overrides
                    props.class,
                )
            "
            v-bind="{ ...forwarded, ...$attrs }"
            @open-auto-focus="onOpenAutoFocus"
        >
            <slot />

            <DialogClose
                v-if="!hideClose"
                class="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none"
            >
                <!-- size-6, matching every other overlay close (MR !312 review). -->
                <X class="h-6 w-6" />
            </DialogClose>
        </DialogContent>
    </DialogPortal>
</template>
